import {
  Background,
  BackgroundVariant,
  EdgeTypes,
  Edge as FlowEdge,
  Node as FlowNode,
  MiniMap,
  NodeTypes,
  ReactFlow,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import { memo, useEffect, useRef, useState } from "react";
import { getLayoutedElements, getNodeHeight } from "./utils/layout-elements";

export const BaseReactFlow = memo(
  function BaseReactFlow({
    initialNodes,
    initialEdges,
    nodeTypes,
    edgeTypes,
    onNodeClick,
    showMiniMap,
    onNodeMouseEnter,
    onNodeMouseLeave,
    onPaneClick,
    onMouseMove,
    connectionModeNodeClick,
  }: {
    initialNodes: FlowNode[];
    initialEdges: FlowEdge[];
    nodeTypes: NodeTypes;
    edgeTypes: EdgeTypes;
    onNodeClick: (nodeId: string | undefined) => void;
    showMiniMap?: boolean;
    onNodeMouseEnter?: (event: React.MouseEvent, node: FlowNode) => void;
    onNodeMouseLeave?: () => void;
    onPaneClick?: () => void;
    onMouseMove?: (event: React.MouseEvent) => void;
    connectionModeNodeClick?: (event: React.MouseEvent, node: FlowNode) => void;
  }) {
    const { fitView } = useReactFlow();
    const fitViewRef = useRef(fitView);
    fitViewRef.current = fitView;
    const [initialRender, setInitialRender] = useState(true);
    const [showComponent, setShowComponent] = useState(false);
    const [nodes, setNodes] = useState<FlowNode[]>([]);
    const [edges, setEdges] = useState<FlowEdge[]>([]);

    const prevInitialNodesRef = useRef<FlowNode[]>([]);
    const prevInitialEdgesRef = useRef<FlowEdge[]>([]);

    useEffect(() => {
      const prevNodes = prevInitialNodesRef.current;
      const prevEdges = prevInitialEdgesRef.current;
      prevInitialNodesRef.current = initialNodes;
      prevInitialEdgesRef.current = initialEdges;

      // Height is estimated from node data, so a node that grows (e.g. more
      // conditions added in the inspector) must re-run layout for its children.
      const sameNodeStructure =
        prevNodes.length > 0 &&
        prevNodes.length === initialNodes.length &&
        initialNodes.every((n, i) => {
          const prev = prevNodes[i];
          return (
            prev !== undefined &&
            prev.id === n.id &&
            getNodeHeight(prev) === getNodeHeight(n)
          );
        });

      const sameEdgeStructure =
        prevEdges.length === initialEdges.length &&
        initialEdges.every(
          (e, i) =>
            prevEdges[i]?.source === e.source &&
            prevEdges[i]?.target === e.target,
        );

      if (sameNodeStructure && sameEdgeStructure) {
        setNodes((current) =>
          initialNodes.map((n) => {
            const existing = current.find((c) => c.id === n.id);
            return existing ? { ...n, position: existing.position } : n;
          }),
        );
        setEdges(initialEdges);
      } else {
        const { nodes: updatedNodes, edges: updatedEdges } =
          getLayoutedElements(initialNodes, initialEdges);
        setNodes(updatedNodes);
        setEdges(updatedEdges);
      }
    }, [initialNodes, initialEdges]);

    // Hack 1: Show component after initial render to prevent ReactFlow from rendering
    // there was a race condition but by delaying the rendering until after mount, it was fixed.
    useEffect(() => {
      setShowComponent(true);
    }, []);

    // Hack 2: Once component is shown, set initialRender to false to trigger fitView useEffect
    useEffect(() => {
      if (showComponent && initialRender) {
        setInitialRender(false);
      }
    }, [showComponent, initialRender]);

    // Hack 3: Set a timeout to make sure new nodes are rendered before calling fitView
    useEffect(() => {
      if (initialRender === false) {
        setTimeout(async () => {
          await fitViewRef.current({ maxZoom: 1 });
        }, 100);
      }
    }, [initialRender]);

    return showComponent ? (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          opacity: initialRender ? 0 : 1,
          transition: "opacity 0.5s ease-in-out",
        }}
      >
        <ReactFlow
          edges={edges}
          edgeTypes={edgeTypes}
          nodeOrigin={[0.5, 0]}
          nodes={nodes}
          nodesDraggable={false}
          nodeTypes={nodeTypes}
          onMouseMove={onMouseMove}
          onNodeClick={(e, node) => {
            // If in connection mode, use the connection mode click handler
            if (connectionModeNodeClick) {
              connectionModeNodeClick(e, node);
              return;
            }

            // Regular mode: Check the data-selectable attribute from the DOM element
            // Need to look inside the node wrapper as React Flow wraps our component
            const nodeElement = document.querySelector(
              `[data-id="${node.id}"] [data-selectable]`,
            );
            const isSelectable =
              nodeElement?.getAttribute("data-selectable") !== "false";

            if (isSelectable) {
              onNodeClick(node.id);
            } else {
              onNodeClick(undefined);
            }
          }}
          onNodeMouseEnter={onNodeMouseEnter}
          onNodeMouseLeave={onNodeMouseLeave}
          onPaneClick={onPaneClick || (() => onNodeClick(undefined))}
          panOnScroll
          proOptions={{ hideAttribution: true }}
          zoomOnDoubleClick={false}
        >
          <Background gap={16} size={1} variant={BackgroundVariant.Dots} />
          {showMiniMap && nodes.length > 5 && <MiniMap />}
        </ReactFlow>
      </div>
    ) : null;
  },
  (prevProps, nextProps) => {
    if (
      prevProps.initialEdges.length !== nextProps.initialEdges.length ||
      prevProps.initialNodes.length !== nextProps.initialNodes.length
    ) {
      return false;
    }

    // These functions aren't stable across renders, so a better fix would be the make them so, however,
    // as dependencies of these are props at advances flow editor implementation, that path would be a fairly
    // large lift. However, it seems that these handlers are expected to change based on other values within
    // the edges and nodes, so changes should still propagate as expected.
    const functionOmit = [
      "data.deleteElements",
      "data.elementDropped",
      "data.updateStep",
    ];
    if (
      prevProps.initialEdges.some(
        (edge, index) =>
          !isEqual(
            omit(edge, functionOmit),
            omit(nextProps.initialEdges[index], functionOmit),
          ),
      ) ||
      prevProps.initialNodes.some(
        (node, index) =>
          !isEqual(
            omit(node, functionOmit),
            omit(nextProps.initialNodes[index], functionOmit),
          ),
      )
    ) {
      return false;
    }

    const deepCompareOmit = ["initialEdges", "initialNodes"];
    const prevPropsOmitted = omit(prevProps, deepCompareOmit);
    const nextPropsOmitted = omit(nextProps, deepCompareOmit);
    if (!isEqual(prevPropsOmitted, nextPropsOmitted)) {
      return false;
    }

    return true;
  },
);
